import { Link } from "react-router-dom";
import { ArrowRight, MonitorSmartphone, RefreshCw, Globe, Nfc, Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { FinalCta } from "@/components/FinalCta";
import { useLanguage } from "../i18n";

const SERVICES = [
  {
    icon: MonitorSmartphone,
    title: "Realizzazione siti web",
    desc: "Siti web moderni e professionali, progettati per presentare chiaramente la tua attività e aiutare i clienti a scoprire i tuoi servizi.",
    points: [
      "Presentazione professionale della tua attività",
      "Informazioni chiare sui tuoi servizi",
      "Perfetto su smartphone, tablet e computer",
      "Navigazione semplice e immediata",
      "Contatti sempre in evidenza",
      "Siti e-commerce, su richiesta",
    ],
    cta: "Richiedi il tuo sito web",
    to: "/siti-web",
  },
  {
    icon: RefreshCw,
    title: "Redesign siti web",
    desc: "Dai al tuo sito esistente una struttura più chiara, un aspetto più moderno e un'esperienza migliore su ogni dispositivo.",
    points: [
      "Identità visiva aggiornata",
      "Migliore esperienza su dispositivi mobili",
      "Navigazione e contenuti più chiari",
      "Call to action più efficaci",
      "Branding più coerente",
    ],
    cta: "Richiedi il redesign",
    to: "/contatti",
  },
  {
    icon: Globe,
    title: "Presenza digitale",
    desc: "Soluzioni digitali pratiche che aiutano le piccole imprese a presentarsi online in modo coerente e professionale.",
    points: [
      "Strategia per la presenza online",
      "Miglioramento del sito web",
      "Esperienza utente e conversioni",
      "Posizionamento online e SEO",
      "Scelta degli strumenti digitali",
      "Analisi delle esigenze aziendali",
    ],
    cta: "Prenota una consulenza",
    to: "/prenota",
  },
  {
    icon: Nfc,
    title: "Recensioni NFC",
    desc: "Carte e adesivi NFC semplici da usare: avvicinando lo smartphone, i clienti raggiungono subito la tua pagina delle recensioni Google.",
    points: [
      "Carte e adesivi personalizzati per la tua attività",
      "Attivazione con un semplice tocco",
      "Nessuna app necessaria per i clienti",
      "Ideali per ristoranti, saloni e negozi",
    ],
    cta: "Richiedi informazioni",
    to: "/recensioni-nfc",
  },
];

export default function Servizi() {
  const { t } = useLanguage();
  return (
    <>
      <Seo
        title={`${t("Servizi")} — Alsera Studio`}
        description={t("Ogni progetto è diverso. Per questo offriamo servizi flessibili, progettati intorno ai tuoi obiettivi.")}
      />
      <section className="pt-32 pb-14 sm:pt-40" data-testid="servizi-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("I nostri servizi")}</p>
            <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.1] tracking-tight text-antracite sm:text-5xl">
              {t("Soluzioni digitali pensate per te.")}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-antracite/70 sm:text-lg">
              {t("Ogni progetto è diverso. Per questo offriamo servizi flessibili, progettati intorno ai tuoi obiettivi, e li adattiamo nel tempo alle esigenze della tua attività.")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-28">
        <div className="mx-auto max-w-7xl space-y-8 px-5 sm:px-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={0.05}>
              <article
                className={`card-soft noise-overlay grid gap-10 overflow-hidden p-8 sm:p-12 lg:grid-cols-[1fr_1fr] ${i % 2 === 1 ? "bg-acqua/30" : ""}`}
                data-testid={`servizio-${i}`}
              >
                <div>
                  <div className="mb-6 inline-flex rounded-2xl bg-petrolio p-3.5 text-avorio">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h2 className="text-2xl font-bold tracking-tight text-antracite sm:text-3xl">{t(s.title)}</h2>
                  <p className="mt-4 max-w-lg text-base leading-relaxed text-antracite/70">{t(s.desc)}</p>
                  <Link to={s.to} className="btn-primary mt-8" data-testid={`servizio-cta-${i}`}>
                    {t(s.cta)}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <ul className="grid content-center gap-3 sm:grid-cols-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 rounded-2xl bg-acqua/50 p-4">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-petrolio" />
                      <span className="text-sm font-medium text-antracite/80">{t(p)}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
          <Reveal>
            <p className="text-center text-sm text-antracite/55" data-testid="servizi-nota">
              {t("Le nostre offerte sono flessibili e vengono aggiornate nel tempo: raccontaci la tua esigenza e valutiamo insieme la soluzione più adatta.")}
            </p>
          </Reveal>
        </div>
      </section>

      <FinalCta />
    </>
  );
}
